'use client';

import { Builder } from '@builder.io/react/lite';
import dynamic from 'next/dynamic';

import { imageConfig } from 'molecules/Image/config';
import podcastConfig from 'molecules/Podcast/config';
import quizConfig from 'molecules/Quiz/config';
import richTextBuilderConfig from 'molecules/RichText/config';
import tableBuilderConfig from 'molecules/Table/config';
import downloadBuilderConfig from 'molecules/TheDownloader/config';
import videoConfig from 'molecules/Video/config';
import FooterNoteConfig from 'molecules/footerNote/config';

import contentEditorConfig from 'components/ContentEditor/config';
import conversionPanelConfig from 'components/ConversionPanel/config';
import faqConfig from 'components/FAQ/config';
import faqPageConfig from 'components/FAQPage/config';
import agentQuoteConfig from 'components/Forms/AgentQuote/config';
import contactFormConfig from 'components/Forms/ContactForm/config';
import dataPrivacyFormConfig from 'components/Forms/DataPrivacy/config';
import techSupportConfig from 'components/Forms/TechSupport/config';
import gridLeadershipConfig from 'components/GridLeadership/config';
import lightboxHeroConfig from 'components/Heros/LightboxHero/config';
import pageHeaderHeroConfig from 'components/Heros/PageHeaderHero/config';
import quoteHeroConfig from 'components/Heros/QuoteHero/config';
import sideNavHeroConfig from 'components/Heros/SideNavHero/config';
import standardHeroConfig from 'components/Heros/StandardHero/config';
import iconCardsConfig from 'components/IconCards/config';
import imageCardsConfig from 'components/ImageCards/config';
import imageMarqueConfig from 'components/ImageMarque/config';
import leaderShipDeckConfig from 'components/LeaderShipDeck/config';
import leadershipConfig from 'components/Leadership/config';
import mediaCoverageConfig from 'components/MediaCoverage/config';
import metricsConfig from 'components/Metrics/config';
import productListingConfig from 'components/ProductListing/config';
import resourceCarouselConfig from 'components/ResourceCarousel/config';
import splitContentConfig from 'components/SplitContent/config';
import basicSwitchbackConfig from 'components/Switchbacks/BasicSwitchback/config';
import calloutSwitchbackConfig from 'components/Switchbacks/CalloutSwitchback/config';
import textWithBackGroundImageSwitchbackConfig from 'components/Switchbacks/TextWithBackgroundImage/config';
import textWithForegroundImageSwitchbackConfig from 'components/Switchbacks/TextWithForegroundImage/config';
import tableMarqueConfig from 'components/TableMarque/config';
import testimonialConfig from 'components/Testimonial/config';
import textCardsConfig from 'components/TextCards/config';
import textSectionConfig from 'components/TextSection/config';
import videoComponentConfig from 'components/Video/config';
import twoColumnCardConfig from 'components/twoColumnCards/config';

import type { Component } from '@builder.io/sdk';
import type { FC } from 'react';
import type { IWrapper } from 'types/types';

const templateComponents = [
  {
    component: dynamic(() => import('molecules/TheDownloader')),
    config: downloadBuilderConfig,
  },
  {
    component: dynamic(() => import('molecules/RichText')),
    config: richTextBuilderConfig,
  },
  {
    component: dynamic(() => import('molecules/Image/RichTextImage')),
    config: imageConfig,
  },
  { component: dynamic(() => import('molecules/Video')), config: videoConfig },
  {
    component: dynamic(() => import('molecules/Podcast')),
    config: podcastConfig,
  },
  {
    component: dynamic(() => import('molecules/Quiz')),
    config: quizConfig,
  },
  {
    component: dynamic(() => import('molecules/Table')),
    config: tableBuilderConfig,
  },
];

const simplePageComponents = [
  {
    component: dynamic(() => import('components/ContentEditor')),
    config: contentEditorConfig,
  },
  {
    component: dynamic(() => import('components/ConversionPanel')),
    config: conversionPanelConfig,
  },
  {
    component: dynamic(() => import('components/GridLeadership')),
    config: gridLeadershipConfig,
  },
  {
    component: dynamic(() => import('components/IconCards')),
    config: iconCardsConfig,
  },
  {
    component: dynamic(() => import('components/ImageCards')),
    config: imageCardsConfig,
  },
  {
    component: dynamic(() => import('components/ImageMarque')),
    config: imageMarqueConfig,
  },
  {
    component: dynamic(() => import('components/LeaderShipDeck')),
    config: leaderShipDeckConfig,
  },
  {
    component: dynamic(() => import('components/Leadership')),
    config: leadershipConfig,
  },
  {
    component: dynamic(() => import('components/MediaCoverage')),
    config: mediaCoverageConfig,
  },
  {
    component: dynamic(() => import('components/Metrics')),
    config: metricsConfig,
  },
  {
    component: dynamic(() => import('components/ResourceCarousel')),
    config: resourceCarouselConfig,
  },
  {
    component: dynamic(() => import('components/ProductListing')),
    config: productListingConfig,
  },
  {
    component: dynamic(() => import('components/SplitContent')),
    config: splitContentConfig,
  },
  {
    component: dynamic(() => import('components/FAQ')),
    config: faqConfig,
  },
  {
    component: dynamic(() => import('components/FAQPage')),
    config: faqPageConfig,
  },
  {
    component: dynamic(() => import('molecules/footerNote')),
    config: FooterNoteConfig,
  },
  {
    component: dynamic(() => import('components/TableMarque')),
    config: tableMarqueConfig,
  },
  {
    component: dynamic(() => import('components/SplitContent')),
    config: splitContentConfig,
  },
  {
    component: dynamic(() => import('components/TextCards')),
    config: textCardsConfig,
  },
  {
    component: dynamic(() => import('components/TextSection')),
    config: textSectionConfig,
  },
  {
    component: dynamic(() => import('components/twoColumnCards')),
    config: twoColumnCardConfig,
  },
  {
    component: dynamic(() => import('components/Video')),
    config: videoComponentConfig,
  },
];

const formComponents = [
  {
    component: dynamic(() => import('components/Forms/AgentQuote')),
    config: agentQuoteConfig,
  },
  {
    component: dynamic(() => import('components/Forms/TechSupport')),
    config: techSupportConfig,
  },
  {
    component: dynamic(() => import('components/Forms/ContactForm')),
    config: contactFormConfig,
  },
  {
    component: dynamic(() => import('components/Forms/DataPrivacy')),
    config: dataPrivacyFormConfig,
  },
];

const heroComponents = [
  {
    component: dynamic(() => import('components/Heros/LightboxHero')),
    config: lightboxHeroConfig,
  },
  {
    component: dynamic(() => import('components/Heros/PageHeaderHero')),
    config: pageHeaderHeroConfig,
  },
  {
    component: dynamic(() => import('components/Heros/QuoteHero')),
    config: quoteHeroConfig,
  },
  {
    component: dynamic(() => import('components/Heros/SideNavHero')),
    config: sideNavHeroConfig,
  },
  {
    component: dynamic(() => import('components/Heros/StandardHero')),
    config: standardHeroConfig,
  },
];

const switchbackComponents = [
  {
    component: dynamic(() => import('components/Switchbacks/BasicSwitchback')),
    config: basicSwitchbackConfig,
  },
  {
    component: dynamic(() => import('components/Switchbacks/CalloutSwitchback')),
    config: calloutSwitchbackConfig,
  },
  {
    component: dynamic(() => import('components/Switchbacks/TextWithBackgroundImage')),
    config: textWithBackGroundImageSwitchbackConfig,
  },
  {
    component: dynamic(() => import('components/Switchbacks/TextWithForegroundImage')),
    config: textWithForegroundImageSwitchbackConfig,
  },
  {
    component: dynamic(() => import('components/Testimonial')),
    config: testimonialConfig,
  },
];

export const allComponents = [
  ...heroComponents,
  ...formComponents,
  ...simplePageComponents,
  ...switchbackComponents,
  ...templateComponents,
];

const allMenus = [
  { name: 'HCI Template Components', components: templateComponents },
  { name: 'M2 Page Components (Simple)', components: simplePageComponents },
  { name: 'M2 Form Components', components: formComponents },
  { name: 'M2 Hero Components', components: heroComponents },
  { name: 'M2 Switchback Components', components: switchbackComponents },
];

const generateComponents = () => {
  allComponents.map(component => Builder.registerComponent(component.component, component.config as Component));
};

const generateMenus = () => {
  allMenus.map(menu =>
    Builder.register('insertMenu', {
      name: menu.name,
      items: menu.components.map(component => ({ name: component.config.name })),
    }),
  );
};

export const RegisterComponents: FC<IWrapper> = ({ children }) => {
  generateComponents();
  generateMenus();

  return children;
};

export default RegisterComponents;
